import { createGraphqlFetcher } from '@apis/createFetcher';

type IntegrationPlatform = 'GA4' | 'shopify';

export interface Integration {
    platform: IntegrationPlatform;
    profileId: string;
    enable: boolean;
    properties: string[];
    lastEnableTime?: number;
    connectionCount?: number | null;
}

// getIntegrationConfigs 返回值
export type IntegrationConfig = {
    id: string;
} & Integration;

// CreateOrUpdateIntegration 输入值
export type UpdateIntegrationInput = { id?: string } & Integration;

const apis = {
    getIntegrationConfigs: {
        name: 'getIntegrationConfigs',
        query: `query getIntegrationConfigs($profileId:String!) {
            getIntegrationConfigs(profileId:$profileId) {
                id
                platform
                profileId
                enable
                properties
                lastEnableTime
                connectionCount
            }
        }`
    },

    createOrUpdateIntegrationConfig: {
        name: 'createOrUpdateIntegrationConfig',
        query: `mutation createOrUpdateIntegrationConfig($input:UpdateIntegrationInput!) {
            createOrUpdateIntegrationConfig(input:$input)
        }`
    },

    getIntegrationApiList: {
        name: 'getAllAPIKeys',
        query: `query getAllAPIKeys($profileId:String!){
            getAllAPIKeys(profileId:$profileId) {
                id
                profileId
                name
                createTime
                updateTime
                createBy
                status
                secretAsterisk
            }
        }`
    },

    createAPIKey: {
        name: 'createAPIKey',
        query: `mutation createAPIKey($input: APIKeyInput!){
            createAPIKey(input:$input)
        }`
    },

    deleteAPIKey: {
        name: 'deleteAPIKey',
        query: `mutation deleteAPIKey($id: ID!){
            deleteAPIKey(id:$id)
        }`
    },

    firstGuideProfile: {
        name: 'getFirstGuideByProfileId',
        query: `query getFirstGuideByProfileId($profileId:String!){
            getFirstGuideByProfileId(profileId:$profileId) {
                status guideKey
            }
        }`
    },

    updateFirstGuideProfile: {
        name: 'updateFirstGuideByProfileId',
        query: `mutation updateFirstGuideByProfileId($input:UpdateFirstGuideInput!){
            updateFirstGuideByProfileId(input:$input)
        }`
    },
    // 添加新的管理角色
    addUnionCollaborativeUser: {
        name: 'addUnionCollaborativeUser',
        query: `mutation addUnionCollaborativeUser($input: AddUnionCollaborativeUserInput!) {
            addUnionCollaborativeUser(input: $input)
        }`
    },

    // 删除当前管理角色
    deleteUnionCollaborativeUser: {
        name: 'deleteUnionCollaborativeUser',
        query: `mutation deleteUnionCollaborativeUser($aid: String!, $profileId: String!) {
            deleteUnionCollaborativeUser(aid: $aid, profileId: $profileId)
        }`
    },

    // 更新当前的管理角色
    updateUnionCollaborativeUser: {
        name: 'updateUnionCollaborativeUser',
        query: `mutation updateUnionCollaborativeUser($input: CollaborativeUserUpdateInput!) {
            updateUnionCollaborativeUser(input: $input)
        }`
    },
    // 重新发送邮件
    resendCollaborativeUserEmail: {
        name: 'resendCollaborativeUserEmail',
        query: `mutation resendCollaborativeUserEmail($aid: String!, $profileId: String!, $collaborativeEmail: String!) {
            resendCollaborativeUserEmail(aid: $aid, profileId: $profileId, collaborativeEmail: $collaborativeEmail)
        }`
    },
    // 获取新用户管理的角色列表 status --->  1:点击了邀请链接， 2: 未点击链接
    getUnionCollaborativeUser: {
        name: 'getUnionCollaborativeUser',
        query: `query getUnionCollaborativeUser($profileId: String!) {
            getUnionCollaborativeUser(profileId: $profileId) {
                id
                aid
                email
                name
                role
                status
                lastActiveTime
                isNpPackage
                area
            }
        }`
    },
    // 获取可邀请套餐的协作用户数量
    getCollaborativeUserCount: {
        name: 'getCollaborativeUserCount',
        query: `query getCollaborativeUserCount($profileId: String!) {
            getCollaborativeUserCount(profileId: $profileId)
        }`
    },
    // 转交当前owner
    transferOwner: {
        name: 'transferOwner',
        query: `mutation transferOwner($aid: String!, $profileId: String!, $password: String!, $email: String!) {
            transferOwner(aid: $aid, profileId:$profileId, password:$password, email:$email)
        }`
    },
    // 获取shopify列表
    getConnectionList: {
        name: 'getConnectionList',
        query: `query getConnectionList($profileId: String!, $platform: IntegrationPlatformEnum!) {
           getConnectionList(profileId: $profileId, platform: $platform) {
                id
                name
                createTime
                creator
           }
        }`
    },
    // 创建shopify connection
    createIntegrationConnection: {
        name: 'createIntegrationConnection',
        query: `mutation createIntegrationConnection($profileId: String!, $platform: IntegrationPlatformEnum!, $config: JSON){
            createIntegrationConnection(profileId: $profileId, platform: $platform, config: $config)
        }`
    },
    // 删除shopify connection
    deleteIntegrationConnection: {
        name: 'deleteIntegrationConnection',
        query: `mutation deleteIntegrationConnection($profileId: String!, $id: ID!, $platform: IntegrationPlatformEnum!) {
            deleteIntegrationConnection(profileId: $profileId, id: $id, platform: $platform)
        }`
    },
    getConnectionById: {
        name: 'getConnectionById',
        query: `query getConnectionById($id: ID!) {
            getConnectionById(id: $id) {
                id,
                name,
                connectionConfig
            }
        }`
    },
    // 更新shopify connection
    updateIntegrationConnection: {
        name: 'updateIntegrationConnection',
        query: `mutation updateIntegrationConnection($profileId: String!, $id: ID!, $config: JSON, $platform: IntegrationPlatformEnum!) {
            updateIntegrationConnection(profileId: $profileId, id: $id,config: $config, platform: $platform)
        }`
    },
    // 校验shopify connection
    verifyConnection: {
        name: 'verifyConnection',
        query: `query verifyConnection($platform: IntegrationPlatformEnum!, $config: JSON!) {
            verifyConnection(platform: $platform, config: $config)
        }`
    },
    // 获取Campaign
    getConnectionEngageList: {
        name: 'getConnectionEngageList',
        query: `query getConnectionEngageList($connectionId: ID!){
            getConnectionEngageList(connectionId: $connectionId) {
                engageId
                engageName
                engageStatus
            }
        }`
    }
};

export default createGraphqlFetcher(apis);
